import type {
  JobCountResponseDto,
  JobFiltersRequestDto,
  JobFiltersResponseDto,
  JobItemDto,
  JobsResponseDto,
} from "@artha/api-contracts";
import type { CreatorProfileForMyLink } from "@artha/types";
import { normalizeNicheValue } from "./normalizers";
import type { JobIndexParams } from "./types";

export type JobIndexFetchOptions = RequestInit & {
  baseUrl?: string;
  parseJson?: boolean;
  next?: unknown;
};

export interface JobIndexClientOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  defaultRequestInit?: RequestInit;
}

export type JobsApiResponse =
  | JobsResponseDto
  | { data: JobsResponseDto | JobItemDto[] }
  | {
    jobs: JobsResponseDto["items"];
    total?: number;
    limit?: number;
    offset?: number;
    has_more?: boolean;
  };

export type JobBySlugItemDto = JobItemDto & {
  requested_job_expired?: boolean;
  resolved_via_alternative?: boolean;
};

export type JobBySlugResponse = JobBySlugItemDto | { data: JobBySlugItemDto };

export type JobSuggestionDto = {
  text: string;
  type: string;
  count: number;
};

export type JobSuggestionsResponse = {
  suggestions: JobSuggestionDto[];
  query_time_ms?: number;
};

export type ResolveJobsByRefIdsRequestBody = {
  ref_ids: string[];
};

export type HistoryRecommendationSeedJob = {
  ref_id: string;
  valid_clicks: number;
  approved_applications: number;
  earnings: number;
  title: string;
  company?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
};

export type RecommendByHistoryRequestBody = {
  seed_jobs: HistoryRecommendationSeedJob[];
  exclude_ref_ids?: string[];
  limit?: number;
  sort_by?: string;
  dedup_by?: string;
  geo_boost?: boolean;
  debug?: boolean;
  include_highlights?: boolean;
};

export type RecommendRequestBody = {
  query: string;
  portal_id?: string;
  geo_boost?: boolean;
  categories?: string[];
  excluded_categories?: string[];
  niche_keywords?: string[];
  negative_keywords?: string[];
  location?: string;
  state?: string;
  city?: string;
  job_type?: string;
  work_mode?: string;
  remote?: boolean;
  exp_min?: number;
  exp_max?: number;
  salary_min?: number;
  salary_max?: number;
  education?: string;
  cpa_min?: number;
  cpa_max?: number;
  company?: string;
  industry?: string;
  domain?: string;
  headcount_min?: number;
  headcount_max?: number;
  revenue?: string;
  only_enriched?: boolean;
  only_cpa?: boolean;
  skills?: string[];
  exp_level?: string[];
  sort_by?: string;
  dedup_by?: string;
  page: number;
  limit: number;
};

export type UserSearchRequestBody = Omit<RecommendRequestBody, "query" | "page" | "limit"> & {
  query?: string;
  page?: number;
  limit?: number;
};

export interface ApiResult<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
}

export type FlexibleJobParams = JobIndexParams & {
  q?: string;
  keyword?: string;
  keywords?: string;
  title?: string;
  exp_min?: string | number;
  exp_max?: string | number;
  negative_keywords?: string;
};

type RecommendOptions = {
  onlyEnriched?: boolean;
  creator?: CreatorProfileForMyLink | { id?: string; _id?: string; settings?: CreatorProfileForMyLink["settings"] } | null;
  geoBoost?: boolean;
};

const JOB_SEARCH_FIELDS = [
  "q",
  "keyword",
  "keywords",
  "title",
  "company",
  "state",
  "city",
  "exp_min",
  "exp_max",
  "categories",
  "niche_keywords",
  "job_type",
  "work_mode",
  "exp_level",
  "education",
  "industry",
  "sort_by",
] as const;

const NUMERIC_FIELDS = ["salary_min", "salary_max", "limit", "offset"] as const;

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, "");

/**
 * Converts a value to an array of strings. 
 * Handles already-array values or comma-separated strings.
 */
export const toParamArray = (value: unknown): string[] | undefined => {
  if (!value) return undefined;

  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  return String(value)
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);
};

/**
 * Normalizes categories by applying niche-specific normalization.
 */
export const normalizeCategoriesParam = (value: unknown): string | undefined => {
  const entries = toParamArray(value);
  if (!entries?.length) return undefined;

  const normalized = entries.map((entry) => normalizeNicheValue(entry)).filter(Boolean);
  return normalized.length > 0 ? normalized.join(",") : undefined;
};

/**
 * Normalizes salary minimum values to ensure they are valid positive numbers.
 */
export const normalizeSalaryMinParam = (value: unknown): unknown => {
  if (value === undefined) return undefined;

  const numericSalaryMin = Number(value);
  if (!Number.isNaN(numericSalaryMin) && numericSalaryMin <= 0) {
    return undefined;
  }

  return value;
};

/**
 * Builds a URL query string from an object of parameters.
 */
export const buildQueryString = (params: object): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.set(key, String(value));
    }
  });
  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
};

const isUSOnlyLocation = (location: unknown) => {
  const locations = String(location ?? "US")
    .toUpperCase()
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

  return locations.length === 1 && locations[0] === "US";
};

const getCreatorId = (creator?: RecommendOptions["creator"]) => {
  if (!creator) return undefined;
  return "id" in creator && creator.id ? creator.id : "_id" in creator && creator._id ? String(creator._id) : undefined;
};

const getCreatorNiches = (creator?: RecommendOptions["creator"]) => {
  if (!creator || !("settings" in creator)) return [];
  return creator.settings?.nicheCategories?.map((entry) => normalizeNicheValue(entry)).filter(Boolean) ?? [];
};

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? undefined : numeric;
};

/**
 * Constructs the request body for the job recommendation API.
 * Maps flexible input parameters to the structured API contract.
 */
export function buildRecommendRequestBody(
  params: FlexibleJobParams = {},
  options: RecommendOptions = {}
): RecommendRequestBody {
  const { onlyEnriched = false, creator, geoBoost = false } = options;
  const limit = params.limit ?? 20;
  const offset = params.offset ?? 0;
  const resolvedLocation = params.location ?? "US";
  const body: RecommendRequestBody = {
    query: String(params.q || params.keyword || params.keywords || params.title || ""),
    portal_id: getCreatorId(creator),
    geo_boost: geoBoost,
    page: Math.floor(offset / limit) + 1,
    limit,
    location: resolvedLocation,
    only_enriched: false,
    only_cpa: false,
  };

  const normalizedCategories = normalizeCategoriesParam(params.categories);
  const categories = toParamArray(normalizedCategories);
  if (categories?.length) body.categories = categories;

  const mergedNicheKeywords = [
    ...getCreatorNiches(creator),
    ...(toParamArray(params.niche_keywords) ?? []),
  ].filter(Boolean);
  if (mergedNicheKeywords.length > 0) body.niche_keywords = mergedNicheKeywords;

  if (params.state) body.state = params.state;
  if (params.city) body.city = params.city;
  if (params.job_type) body.job_type = toParamArray(params.job_type)?.[0];
  if (params.work_mode) body.work_mode = toParamArray(params.work_mode)?.[0];
  if (params.exp_level) body.exp_level = toParamArray(params.exp_level);
  if (params.exp_min !== undefined) body.exp_min = toOptionalNumber(params.exp_min);
  if (params.exp_max !== undefined) body.exp_max = toOptionalNumber(params.exp_max);
  if (params.education) body.education = toParamArray(params.education)?.[0];
  if (params.industry) body.industry = toParamArray(params.industry)?.[0];
  if (params.company) body.company = toParamArray(params.company)?.[0];

  const normalizedSalaryMin = normalizeSalaryMinParam(params.salary_min);
  if (normalizedSalaryMin !== undefined) body.salary_min = Number(normalizedSalaryMin);
  if (params.salary_max !== undefined) body.salary_max = params.salary_max;
  if (params.sort_by) body.sort_by = params.sort_by;
  if (params.dedup_by) body.dedup_by = params.dedup_by;
  if (params.negative_keywords) body.negative_keywords = toParamArray(params.negative_keywords);
  if (onlyEnriched && body.only_cpa) body.only_enriched = true;

  return Object.fromEntries(Object.entries(body).filter(([, value]) => value !== undefined)) as RecommendRequestBody;
}

/**
 * Builds the request body for searching recommendations.
 * Extends the basic recommendation body with search-specific sorting and deduplication.
 */
export function buildRecommendSearchBody(
  params: FlexibleJobParams = {},
  options: RecommendOptions & { sortBy?: string; dedupBy?: string } = {}
): UserSearchRequestBody {
  const body = buildRecommendRequestBody(params, options);
  return Object.fromEntries(
    Object.entries({
      ...body,
      query: body.query || undefined,
      sort_by: options.sortBy || params.sort_by || "most_relevant",
      dedup_by: options.dedupBy || params.dedup_by || "dedup_key",
    }).filter(([, value]) => value !== undefined)
  ) as UserSearchRequestBody;
}

/**
 * Builds the query parameters for board-level job searches.
 * Handles normalization of categories and salary filters.
 */
export function buildBoardSearchQueryParams(
  params: FlexibleJobParams = {},
  options: { defaultLocation?: string; dedupDefault?: string; onlyEnriched?: boolean } = {}
): Record<string, unknown> {
  const { defaultLocation = "US", dedupDefault = "content_hash", onlyEnriched = false } = options;
  const queryParams: Record<string, unknown> = {};

  for (const key of JOB_SEARCH_FIELDS) {
    const value = params[key];
    if (!value) continue;

    if (key === "categories") {
      const normalizedCategories = normalizeCategoriesParam(value);
      if (normalizedCategories) queryParams.categories = normalizedCategories;
      continue;
    }

    queryParams[key] = value;
  }

  for (const key of NUMERIC_FIELDS) {
    const value = params[key];
    if (value === undefined) continue;

    if (key === "salary_min") {
      const normalizedSalaryMin = normalizeSalaryMinParam(value);
      if (normalizedSalaryMin === undefined) continue;
      queryParams[key] = normalizedSalaryMin;
      continue;
    }

    queryParams[key] = value;
  }

  const resolvedLocation = params.location ?? defaultLocation;
  queryParams.location = resolvedLocation;
  queryParams.dedup_by = params.dedup_by ?? dedupDefault;
  queryParams.only_enriched = onlyEnriched && isUSOnlyLocation(resolvedLocation);
  queryParams.only_cpa = false;

  return queryParams;
}

/**
 * Normalizes a raw jobs API response into a standard JobsResponseDto.
 * Handles variations in API response formats.
 */
export function normalizeJobsResponse(payload?: JobsApiResponse): JobsResponseDto | undefined {
  if (!payload) return undefined;

  if ("data" in payload) {
    if (Array.isArray(payload.data)) {
      return {
        items: payload.data,
        total: payload.data.length,
        limit: payload.data.length,
        offset: 0,
        has_more: false,
      };
    }
    return payload.data;
  }

  if ("jobs" in payload) {
    return {
      items: payload.jobs ?? [],
      total: payload.total ?? payload.jobs?.length ?? 0,
      limit: payload.limit ?? payload.jobs?.length ?? 0,
      offset: payload.offset ?? 0,
      has_more: payload.has_more ?? false,
    };
  }

  return payload;
}

/**
 * Normalizes a job-by-slug response.
 */
export function normalizeJobBySlugResponse(payload?: JobBySlugResponse): JobBySlugItemDto | undefined {
  if (!payload) return undefined;
  if ("data" in payload) return payload.data;
  return payload;
}

/**
 * Normalizes a job count response.
 */
export function normalizeJobCountResponse(payload?: Partial<JobCountResponseDto>): JobCountResponseDto | undefined {
  if (!payload) return undefined;
  return {
    count: payload.count ?? 0,
    query_time_ms: payload.query_time_ms,
  };
}

/**
 * Normalizes a job filters response to ensure all fields are present and correctly typed.
 */
export function normalizeJobFiltersResponse(payload?: Partial<JobFiltersResponseDto>): JobFiltersResponseDto | undefined {
  if (!payload) return undefined;
  return {
    categories: payload.categories || [],
    job_types: payload.job_types || [],
    experience_levels: payload.experience_levels || [],
    education_levels: payload.education_levels || [],
    work_modes: payload.work_modes || [],
    countries: payload.countries || [],
    states: payload.states || [],
    cities: payload.cities || [],
    companies: payload.companies || [],
    industries: payload.industries || [],
    salary_ranges: payload.salary_ranges || null,
    total_jobs: payload.total_jobs || 0,
    query_time_ms: payload.query_time_ms || 0,
  };
}

/**
 * Builds the request body for fetching job filter options (POST).
 */
export function buildFilterRequestBody(params: Partial<JobFiltersRequestDto> = {}) {
  return {
    categories: toParamArray(params.categories) ?? [],
    niche_keywords: toParamArray(params.niche_keywords) ?? ["react", "javascript", "typescript"],
    location: params.location || "US",
    include_categories: params.include_categories ?? true,
    include_job_types: params.include_job_types ?? true,
    include_work_modes: params.include_work_modes ?? true,
    include_education_levels: params.include_education_levels ?? true,
    include_experience_levels: params.include_experience_levels ?? true,
    include_salary_ranges: true,
    include_countries: params.include_countries ?? true,
    include_states: params.include_states ?? false,
    include_cities: params.include_cities ?? false,
    include_companies: params.include_companies ?? true,
    include_industries: params.include_industries ?? true,
    categories_size: 50,
    job_types_size: 10,
    work_modes_size: 10,
    countries_size: 50,
    states_size: 100,
    cities_size: 100,
    companies_size: 50,
    industries_size: 50,
  };
}

/**
 * Builds the query parameters for fetching job filter options (GET).
 */
export function buildFilterQueryParams(params: Partial<JobFiltersRequestDto> = {}) {
  return {
    categories: params.categories,
    niche_keywords: params.niche_keywords,
    location: params.location,
    include_categories: params.include_categories ?? true,
    include_job_types: params.include_job_types ?? true,
    include_work_modes: params.include_work_modes ?? true,
    include_education_levels: params.include_education_levels ?? true,
    include_experience_levels: params.include_experience_levels ?? true,
    include_salary_ranges: true,
    include_countries: params.include_countries ?? true,
    include_states: params.include_states ?? true,
    include_cities: params.include_cities ?? true,
    include_companies: params.include_companies ?? true,
    include_industries: params.include_industries ?? true,
    categories_size: 50,
    job_types_size: 10,
    work_modes_size: 10,
    countries_size: 50,
    states_size: 100,
    cities_size: 100,
    companies_size: 50,
    industries_size: 50,
  };
}

/**
 * Factory function to create a JobIndexClient.
 * Provides methods for searching jobs, fetching recommendations, and managing filters.
 */
export function createJobIndexClient({ baseUrl, fetchImpl = fetch, defaultRequestInit = {} }: JobIndexClientOptions) {
  const resolvedBaseUrl = trimTrailingSlash(baseUrl);

  /**
   * Generic internal request helper for the Jobs API.
   * Handles base URL resolution, headers, and JSON parsing.
   */
  const request = async <T>(path: string, options: JobIndexFetchOptions = {}): Promise<ApiResult<T>> => {
    const { parseJson = true, baseUrl: requestBaseUrl, ...init } = options;
    const targetBaseUrl = trimTrailingSlash(requestBaseUrl ?? resolvedBaseUrl);
    const response = await fetchImpl(`${targetBaseUrl}${path}`, {
      cache: "no-store",
      ...defaultRequestInit,
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...(defaultRequestInit.headers || {}),
        ...(init.headers || {}),
      },
    });

    let data: T | undefined;
    if (parseJson) {
      try {
        data = (await response.json()) as T;
      } catch {
        data = undefined;
      }
    }

    return { ok: response.ok, status: response.status, data };
  };

  /**
   * Fetches jobs using a board-style search (GET).
   */
  const fetchJobs = async (params: FlexibleJobParams, options: JobIndexFetchOptions = {}) => {
    const result = await request<JobsApiResponse>(`/jobs/board-search${buildQueryString(params)}`, options);
    const data = normalizeJobsResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch jobs");
    return data;
  };

  /**
   * Fetches personalized job recommendations for a creator (POST).
   */
  const fetchRecommendJobs = async (
    params: FlexibleJobParams = {},
    recommendOptions: RecommendOptions = {},
    requestOptions: JobIndexFetchOptions = {}
  ) => {
    const result = await request<JobsApiResponse>("/recommend", {
      method: "POST",
      body: JSON.stringify(buildRecommendRequestBody(params, recommendOptions)),
      ...requestOptions,
    });
    const data = normalizeJobsResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch recommended jobs");
    return data;
  };

  /**
   * Searches within a creator's personalized recommendations (POST).
   */
  const fetchRecommendSearch = async (
    params: FlexibleJobParams = {},
    recommendOptions: RecommendOptions & { sortBy?: string; dedupBy?: string } = {},
    requestOptions: JobIndexFetchOptions = {}
  ) => {
    const result = await request<JobsApiResponse>("/recommend/search", {
      method: "POST",
      body: JSON.stringify(buildRecommendSearchBody(params, recommendOptions)),
      ...requestOptions,
    });
    const data = normalizeJobsResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch recommend search results");
    return data;
  };

  /**
   * Resolves jobs from a ranked list of ref_ids, using archived fallbacks when needed.
   */
  const resolveJobsByRefIds = async (
    refIds: string[],
    requestOptions: JobIndexFetchOptions = {}
  ) => {
    const body: ResolveJobsByRefIdsRequestBody = { ref_ids: refIds };
    const result = await request<JobsApiResponse>("/jobs/resolve-refids", {
      method: "POST",
      body: JSON.stringify(body),
      ...requestOptions,
    });
    const data = normalizeJobsResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to resolve jobs by ref ids");
    return data;
  };

  /**
   * Builds recommendations directly from a ranked creator history payload.
   */
  const fetchRecommendByHistory = async (
    body: RecommendByHistoryRequestBody,
    requestOptions: JobIndexFetchOptions = {}
  ) => {
    const result = await request<JobsApiResponse>("/recommend/by-history", {
      method: "POST",
      body: JSON.stringify(body),
      ...requestOptions,
    });
    const data = normalizeJobsResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch recommend by history results");
    return data;
  };

  /**
   * Fetches the total count of jobs matching specific criteria.
   */
  const fetchJobCount = async (params: FlexibleJobParams = {}, options: JobIndexFetchOptions = {}) => {
    const result = await request<JobCountResponseDto>(
      `/jobs/board-search-count${buildQueryString(params)}`,
      options
    );
    const data = normalizeJobCountResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch job count");
    return data;
  };

  /**
   * Fetches available job filters (categories, job types, locations, etc.).
   */
  const fetchFilters = async (
    params: Partial<JobFiltersRequestDto> = {},
    options: JobIndexFetchOptions = {}
  ) => {
    const method = options.method ?? "GET";
    const { method: _method, ...requestOptions } = options;
    const result = await request<JobFiltersResponseDto>(
      method === "POST"
        ? "/jobs/filters"
        : `/jobs/filters${buildQueryString(buildFilterQueryParams(params))}`,
      {
        ...requestOptions,
        method,
        body: method === "POST" ? JSON.stringify(buildFilterRequestBody(params)) : undefined,
      }
    );
    const data = normalizeJobFiltersResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch job filters");
    return data;
  };

  /**
   * Fetches jobs similar to a specific job slug.
   */
  const fetchSimilarJobs = async (
    jobSlug: string,
    limit = 10,
    query?: string,
    location = "US",
    options: JobIndexFetchOptions & { portalId?: string } = {}
  ) => {
    const { portalId, ...requestOptions } = options;
    const queryParams: Record<string, unknown> = { slug: jobSlug, limit, location };
    if (query) queryParams.q = query;
    if (portalId) queryParams.portal_id = portalId;
    const result = await request<JobsApiResponse>(`/jobs/similar${buildQueryString(queryParams)}`, requestOptions);
    const data = normalizeJobsResponse(result.data);
    if (!result.ok || !data) throw new Error("Failed to fetch similar jobs");
    return data;
  };

  /**
   * Fetches a single job by its slug.
   */
  const fetchJobBySlug = async (jobSlug: string, options: JobIndexFetchOptions & { portalId?: string } = {}) => {
    if (!jobSlug) return null;
    const { portalId, ...requestOptions } = options;
    const queryStr = portalId ? `?portal_id=${encodeURIComponent(portalId)}` : "";
    const result = await request<JobBySlugResponse>(`/jobs/slug/${jobSlug}${queryStr}`, requestOptions);
    if (!result.ok) return null;
    return normalizeJobBySlugResponse(result.data) ?? null;
  };

  /**
   * Fetates autocomplete suggestions for job titles.
   */
  const fetchJobSuggestions = async (query: string, limit = 10, options: JobIndexFetchOptions = {}) => {
    if (!query) return [];
    const result = await request<JobSuggestionsResponse>(
      `/jobs/board-search-autocomplete${buildQueryString({ q: query, field: "title", limit })}`,
      options
    );
    if (!result.ok) return [];
    return result.data?.suggestions ?? [];
  };

  return {
    request,
    fetchJobs,
    fetchRecommendJobs,
    fetchRecommendSearch,
    resolveJobsByRefIds,
    fetchRecommendByHistory,
    fetchJobCount,
    fetchFilters,
    fetchSimilarJobs,
    fetchJobBySlug,
    fetchJobSuggestions,
  };
}
